// Watch list (issue 28): the studios the owner follows most closely, one row each, the figures that
// matter side by side. Display only: nothing here collects anything, and a figure that was never
// measured reads "not measured", never 0 (CLAUDE.md: unknown is null).
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Badge, Empty, ErrorMsg, Loading, PageHead, StudioLogo, Time, Unknown } from '../components/ui';
import { api, errorText } from '../lib/api';
import type { CountFigure, WatchKpi, WatchListPayload } from '../lib/deepTypes';
import { COMPLETENESS_LABEL, countryName } from '../lib/format';
import { useApi, useSession } from '../lib/session';

type SortKey = 'name' | 'location' | 'depth' | 'team' | 'last_post' | 'posts_per_week' | 'posts_30d' | 'posts_90d' | 'post_types' | 'tone' | 'changes' | 'pricing' | 'socials' | 'completeness';

const NOT_MEASURED = <Unknown>not measured</Unknown>;

function countCell(f: CountFigure, id: string, tab: string, what: string) {
  const link = (children: React.ReactNode) => <Link to={`/competitors/${id}?tab=${tab}`}>{children}</Link>;
  if (f.value === null) {
    return (
      <>
        {link(NOT_MEASURED)}
        {f.by_hand ? <div className="small muted">{f.by_hand} entered by hand</div> : null}
      </>
    );
  }
  return (
    <>
      {link(<strong className="num">{f.value}</strong>)}
      {f.coverage === 'partial' && (
        <div className="small">
          <Badge tone="warn" title={`${what}: only part of the window was covered by a collection run${f.unmeasured.length ? `; not measured: ${f.unmeasured.join(', ')}` : ''}`}>
            partial
          </Badge>
        </div>
      )}
      {f.by_hand ? <div className="small muted">+{f.by_hand} by hand</div> : null}
    </>
  );
}

const COLUMN_VIEW: Record<string, { label: string; sort: SortKey; render: (k: WatchKpi) => React.ReactNode; className?: string }> = {
  location: {
    label: 'Country / city',
    sort: 'location',
    render: (k) =>
      k.country || k.city ? (
        <Link to={`/competitors/${k.id}`}>
          {k.city ?? <Unknown>city unknown</Unknown>}
          {k.country && <span className="muted">, {countryName(k.country)}</span>}
        </Link>
      ) : (
        <Unknown />
      ),
  },
  depth: { label: 'Research depth', sort: 'depth', render: (k) => (k.research_depth === 'deep' ? <Badge tone="deep">Deep</Badge> : <span className="muted">Light</span>) },
  team: { label: 'Team size', sort: 'team', render: (k) => (k.team_size_band ? <Link to={`/competitors/${k.id}`}>{k.team_size_band}</Link> : <Unknown />), className: 'num' },
  last_post: {
    label: 'Last post',
    sort: 'last_post',
    render: (k) =>
      k.last_post.date ? (
        <Link to={`/competitors/${k.id}?tab=social`}>
          <Time iso={k.last_post.date} />
          {k.last_post.by_hand && <span className="small muted"> · by hand</span>}
        </Link>
      ) : (
        <Link to={`/competitors/${k.id}?tab=social`}>
          <Unknown>no dated post</Unknown>
        </Link>
      ),
  },
  posts_per_week: {
    label: 'Posts / week',
    sort: 'posts_per_week',
    className: 'num',
    render: (k) => {
      const f = k.posts_per_week;
      return f.value === null ? (
        <Link to={`/competitors/${k.id}?tab=social`}>{NOT_MEASURED}</Link>
      ) : (
        <>
          <Link to={`/competitors/${k.id}?tab=social`}>
            <strong>{f.value}</strong>
          </Link>
          {f.unmeasured.length > 0 && <div className="small muted">without {f.unmeasured.join(', ')} (not measured)</div>}
        </>
      );
    },
  },
  posts_30d: { label: 'Posts, 30 days', sort: 'posts_30d', className: 'num', render: (k) => countCell(k.posts_30d, k.id, 'social', '30 days') },
  posts_90d: { label: 'Posts, 90 days', sort: 'posts_90d', className: 'num', render: (k) => countCell(k.posts_90d, k.id, 'social', '90 days') },
  post_types: {
    label: 'Main post types',
    sort: 'post_types',
    render: (k) =>
      k.post_types ? (
        <Link to={`/competitors/${k.id}?tab=social`}>
          {k.post_types.top.length ? k.post_types.top.map((t) => `${t.label} ${t.count}`).join(' · ') : <Unknown>none labelled</Unknown>}
          {k.post_types.status === 'proposed' && <span className="small muted"> · proposed</span>}
        </Link>
      ) : (
        <Link to={`/competitors/${k.id}?tab=social`}>
          <Unknown>no analysis yet</Unknown>
        </Link>
      ),
  },
  tone: {
    label: 'Tone',
    sort: 'tone',
    render: (k) =>
      k.tone && k.tone.traits.length ? (
        <Link to={`/competitors/${k.id}?tab=social`}>{k.tone.traits.join(', ')}</Link>
      ) : (
        <Link to={`/competitors/${k.id}?tab=social`}>
          <Unknown>no analysis yet</Unknown>
        </Link>
      ),
  },
  changes: {
    label: 'New page changes',
    sort: 'changes',
    className: 'num',
    render: (k) => (
      <Link to={`/competitors/${k.id}?tab=changes`}>{k.research_depth !== 'deep' && k.changes_new === 0 ? <Unknown>not watched</Unknown> : k.changes_new > 0 ? <Badge tone="warn">{k.changes_new} to review</Badge> : '0'}</Link>
    ),
  },
  pricing: {
    label: 'Pricing evidence',
    sort: 'pricing',
    render: (k) => (
      <Link to={`/competitors/${k.id}?tab=pricing`}>{k.pricing.state === 'not_checked' ? <Unknown>{k.pricing.label}</Unknown> : k.pricing.label}</Link>
    ),
  },
  socials: {
    label: 'Socials',
    sort: 'socials',
    render: (k) => (
      <Link to={`/competitors/${k.id}?tab=social`} title={k.socials.missing.length ? `missing: ${k.socials.missing.join(', ')}` : 'all known platforms present'}>
        <span className="num">{k.socials.present.length}</span> of {k.socials.present.length + k.socials.missing.length}
        <div className="small muted">{k.socials.present.length ? k.socials.present.join(', ') : 'none recorded'}</div>
      </Link>
    ),
  },
  completeness: { label: 'Completeness', sort: 'completeness', render: (k) => <Link to={`/competitors/${k.id}`}>{COMPLETENESS_LABEL[k.completeness]}</Link> },
};

const COMPLETENESS_RANK = { minimal: 0, partial: 1, complete: 2 } as const;

function sortValue(k: WatchKpi, key: SortKey): string | number | null {
  switch (key) {
    case 'name':
      return k.name.toLowerCase();
    case 'location':
      return k.country ? `${k.country} ${k.city ?? ''}`.toLowerCase() : null;
    case 'depth':
      return k.research_depth === 'deep' ? 1 : 0;
    case 'team':
      return k.team_size_band;
    case 'last_post':
      return k.last_post.date;
    case 'posts_per_week':
      return k.posts_per_week.value;
    case 'posts_30d':
      return k.posts_30d.value;
    case 'posts_90d':
      return k.posts_90d.value;
    case 'post_types':
      return k.post_types?.top[0]?.label.toLowerCase() ?? null;
    case 'tone':
      return k.tone?.traits[0]?.toLowerCase() ?? null;
    case 'changes':
      return k.changes_new;
    case 'pricing':
      return k.pricing.label;
    case 'socials':
      return k.socials.present.length;
    case 'completeness':
      return COMPLETENESS_RANK[k.completeness];
  }
}

export function WatchList() {
  const wl = useApi<WatchListPayload>('/api/watchlist');
  const { user } = useSession();
  const owner = user?.role === 'owner';
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'name', dir: 1 });
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  // The box and the table follow the click at once; the saved list replaces this when the server answers.
  const [pending, setPending] = useState<string[] | null>(null);

  const rows = useMemo(() => {
    const list = [...(wl.data?.studios ?? [])];
    list.sort((a, b) => {
      const x = sortValue(a, sort.key);
      const y = sortValue(b, sort.key);
      // Unmeasured figures sort last in both directions.
      if (x === null && y === null) return a.name.localeCompare(b.name);
      if (x === null) return 1;
      if (y === null) return -1;
      const c = x < y ? -1 : x > y ? 1 : 0;
      return c === 0 ? a.name.localeCompare(b.name) : c * sort.dir;
    });
    return list;
  }, [wl.data, sort]);

  async function saveColumns(columns: string[]) {
    setErr('');
    setMsg('');
    setPending(columns);
    try {
      await api('/api/settings/watchlist', { method: 'PUT', body: { value: { columns } } });
      setMsg('Columns saved.');
      wl.reload();
    } catch (e) {
      setPending(null);
      setErr(errorText(e));
    }
  }
  async function unwatch(k: WatchKpi) {
    setErr('');
    try {
      await api(`/api/studios/${k.id}/watchlist`, { method: 'PUT', body: { watched: false } });
      setMsg(`${k.name} removed from the watch list.`);
      wl.reload();
    } catch (e) {
      setErr(errorText(e));
    }
  }

  const header = (key: SortKey, label: string, className?: string) => (
    <th className={className} aria-sort={sort.key === key ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}>
      <button
        type="button"
        className="link"
        data-sort={key}
        onClick={() => setSort((s) => (s.key === key ? { key, dir: (s.dir * -1) as 1 | -1 } : { key, dir: 1 }))}
      >
        {label}
        {sort.key === key ? (sort.dir === 1 ? ' ▲' : ' ▼') : ''}
      </button>
    </th>
  );

  const cols = pending ?? wl.data?.columns ?? [];
  return (
    <div className="page wide">
      <PageHead title="Watch list" meta={`${rows.length} ${rows.length === 1 ? 'studio' : 'studios'} followed closely`} />
      <p className="muted small">{wl.data?.note}</p>
      <ErrorMsg>{wl.error || err}</ErrorMsg>
      {msg && <p className="msg ok small">{msg}</p>}
      {wl.loading && !wl.data ? (
        <Loading what="Loading the watch list" />
      ) : wl.data && (
        <>
          <details className="filter" style={{ marginBottom: 12 }}>
            <summary>
              Columns <span className="num">· {cols.length} of {wl.data.available.length}</span>
            </summary>
            <div className="filter-pop" role="group" aria-label="Visible columns">
              {wl.data.available.map((c) => (
                <label key={c.id} className="check">
                  <input
                    type="checkbox"
                    checked={cols.includes(c.id)}
                    disabled={!owner}
                    onChange={(e) => {
                      // keep the catalogue order, whatever order the boxes are ticked in
                      const next = wl.data!.available.map((a) => a.id).filter((id) => (id === c.id ? e.target.checked : cols.includes(id)));
                      void saveColumns(next);
                    }}
                  />{' '}
                  {c.label}
                </label>
              ))}
              {!owner && <p className="small muted">Only the owner chooses the columns.</p>}
            </div>
          </details>
          {rows.length === 0 ? (
            <Empty>
              No studio on the watch list yet. Press “Watch” on a studio page, or select studios in <Link to="/competitors">Competitors</Link> and choose “Add to watch list”.
            </Empty>
          ) : (
            <div className="table-wrap">
              <table className="table" data-testid="watch-table">
                <thead>
                  <tr>
                    {header('name', 'Studio')}
                    {cols.filter((c) => COLUMN_VIEW[c]).map((c) => header(COLUMN_VIEW[c].sort, COLUMN_VIEW[c].label, COLUMN_VIEW[c].className))}
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((k) => (
                    <tr key={k.id} data-studio={k.id}>
                      <td className="name-cell" data-label="Studio">
                        <StudioLogo src={k.logo_url} />
                        <Link to={`/competitors/${k.id}`} className="studio-name">
                          {k.name}
                        </Link>
                      </td>
                      {cols.filter((c) => COLUMN_VIEW[c]).map((c) => (
                        <td key={c} data-label={COLUMN_VIEW[c].label} data-col={c} className={COLUMN_VIEW[c].className}>
                          {COLUMN_VIEW[c].render(k)}
                        </td>
                      ))}
                      <td>
                        <button type="button" className="link small" onClick={() => void unwatch(k)} aria-label={`Stop watching ${k.name}`}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
